import {
  type DynamicClient,
  type WalletAccount,
  getActiveNetworkData,
} from '@dynamic-labs-sdk/client';
import { assertDefined } from '@dynamic-labs-sdk/client/core';
import type { WaasProvider } from '@dynamic-labs-sdk/client/waas/core';
import type {
  Account,
  Chain,
  Hex,
  HttpTransportConfig,
  TransactionSerializable,
  Transport,
  TypedData,
  WalletClient,
  WalletClientConfig,
} from 'viem';
import { createWalletClient, http, serializeTransaction } from 'viem';
import type { SignAuthorizationReturnType } from 'viem/accounts';
import { toAccount } from 'viem/accounts';

import { mapNetworkDataToViemChain } from '../../../../viem/utils/mapNetworkDataToViemChain';
import type { SignAuthorizationParametersWithoutPrivateKey } from '../../../../viem/viem.types';
import { signAuthorization } from '../signAuthorization';
import { signTypedData } from '../signTypedData';

type CreateViemWalletClientForWaasParams = {
  httpTransportConfig?: HttpTransportConfig;
  viemWalletClientConfig?: Omit<
    WalletClientConfig,
    'account' | 'chain' | 'transport'
  >;
  waasProvider: WaasProvider;
  walletAccount: WalletAccount;
};

/** @not-instrumented */
// eslint-disable-next-line custom-rules/require-single-object-param
export const createViemWalletClientForWaas = async (
  {
    waasProvider,
    walletAccount,
    httpTransportConfig,
    viemWalletClientConfig,
  }: CreateViemWalletClientForWaasParams,
  dynamicClient: DynamicClient
): Promise<WalletClient<Transport, Chain, Account>> => {
  const { networkData: activeNetworkData } = await getActiveNetworkData(
    { walletAccount },
    dynamicClient
  );

  assertDefined(
    activeNetworkData,
    `No network data found for wallet account ${walletAccount.address}`
  );

  return createWalletClient({
    account: toAccount({
      address: walletAccount.address as Hex,
      async signAuthorization(
        parameters: SignAuthorizationParametersWithoutPrivateKey
      ): Promise<SignAuthorizationReturnType> {
        const signature = await signAuthorization(
          {
            parameters,
            waasProvider,
            walletAccount,
          },
          dynamicClient
        );

        return signature;
      },
      signMessage: async ({ message }) => {
        const { signature } = await waasProvider.signMessage({
          message: message as string,
          walletAccount,
        });

        return signature as Hex;
      },
      signTransaction: async (
        transaction: TransactionSerializable
      ): Promise<Hex> => {
        const { signature } = await waasProvider.signSerializedTransaction({
          serializedTransaction: serializeTransaction(transaction),
          walletAccount,
        });

        return signature as Hex;
      },
      // eslint-disable-next-line custom-rules/require-single-object-param
      signTypedData: async (
        typedData: TypedData | { [key: string]: unknown }
      ): Promise<Hex> => {
        const signature = await signTypedData(
          {
            typedData,
            waasProvider,
            walletAccount,
          },
          dynamicClient
        );

        return signature as Hex;
      },
    }),
    chain: mapNetworkDataToViemChain(activeNetworkData),
    transport: http(activeNetworkData.rpcUrls.http[0], httpTransportConfig),
    ...viemWalletClientConfig,
  });
};
